import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface ErrorLog {
  id: string;
  account_id: string;
  source: string;
  error_code: string | null;
  error_message: string;
  details: string | null;
  http_status: number | null;
  metadata: Record<string, any>;
  created_at: string;
}

const PAGE_SIZE = 50;

export function useErrorLogs() {
  const { user } = useAuth();
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const fetchLogs = useCallback(async (reset = false) => {
    if (!user?.account?.id || !supabase) return;

    try {
      setLoading(true);
      setError(null);

      const currentPage = reset ? 0 : page;

      let query = supabase
        .from('account_error_logs')
        .select('*')
        .eq('account_id', user.account.id)
        .order('created_at', { ascending: false })
        .range(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE - 1);

      if (sourceFilter) {
        query = query.eq('source', sourceFilter);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const results = data || [];
      setHasMore(results.length === PAGE_SIZE);

      if (reset || currentPage === 0) {
        setLogs(results);
        setPage(0);
      } else {
        setLogs(prev => [...prev, ...results]);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.account?.id, sourceFilter, page]);

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const changeSourceFilter = (source: string | null) => {
    setSourceFilter(source);
    setPage(0);
  };

  useEffect(() => {
    fetchLogs(page === 0);
  }, [fetchLogs]);

  return {
    logs,
    loading,
    error,
    hasMore,
    sourceFilter,
    fetchLogs: () => fetchLogs(true),
    loadMore,
    changeSourceFilter
  };
}
